import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { ProcessedOfficer, formatHm } from '../lib/officerUtils';
import { priorDayTeamStats } from '../data/mockOfficers';
interface HeroStoryProps {
  officers: ProcessedOfficer[];
}
function CountUp({
  value,
  duration = 900
}: {
  value: number;
  duration?: number;
}) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(value * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span className="tabular-nums" data-id="element-58">{Math.round(display)}</span>;
}
function Delta({
  value,
  inverse = false
}: {
  value: number;
  inverse?: boolean;
}) {
  const positive = inverse ? value < 0 : value > 0;
  const Icon = value >= 0 ? ArrowUpRight : ArrowDownRight;
  return <span className={`inline-flex items-center gap-0.5 text-xs font-medium tabular-nums ${positive ? 'text-signal' : 'text-ember'}`} data-id="element-59">
      <Icon className="w-3 h-3" strokeWidth={2.5} data-id="element-60" />
      {Math.abs(value).toFixed(1)}%
    </span>;
}
export function HeroStory({
  officers
}: HeroStoryProps) {
  const valid = officers.filter(o => o.loginMin > 0);
  const totalSubmissions = valid.reduce((s, o) => s + o.submissions, 0);
  const totalLeads = valid.reduce((s, o) => s + o.leadsConverted, 0);
  const totalBorrower = valid.reduce((s, o) => s + o.borrowerMin, 0);
  const totalLogin = valid.reduce((s, o) => s + o.loginMin, 0);
  const totalOffQueue = valid.reduce((s, o) => s + o.offQueueMin, 0);
  const avgProduction = valid.reduce((s, o) => s + o.productionScore, 0) / Math.max(valid.length, 1);
  const offQueueRatio = totalLogin > 0 ? totalOffQueue / totalLogin * 100 : 0;
  const lead = [...valid].sort((a, b) => b.productionScore - a.productionScore)[0];
  const submissionsDelta = (totalSubmissions - priorDayTeamStats.totalSubmissions) / priorDayTeamStats.totalSubmissions * 100;
  const leadsDelta = (totalLeads - priorDayTeamStats.totalLeads) / priorDayTeamStats.totalLeads * 100;
  const borrowerDelta = (totalBorrower - priorDayTeamStats.totalBorrowerMin) / priorDayTeamStats.totalBorrowerMin * 100;
  const productionDelta = avgProduction - priorDayTeamStats.avgProductionScore;
  const offQueueDelta = offQueueRatio - priorDayTeamStats.avgOffQueueRatio * 100;
  const liftPct = Math.abs(submissionsDelta).toFixed(0);
  const direction = submissionsDelta >= 0 ? 'lift' : 'dip';
  return <motion.section initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.6,
    delay: 0.15
  }} className="w-full px-8 lg:px-12 xl:px-16 py-16" data-id="element-61">
      <div className="grid grid-cols-12 gap-10 items-end" data-id="element-62">
        <div className="col-span-12 lg:col-span-8" data-id="element-63">
          <div className="font-serif font-bold text-xs tracking-[0.28em] uppercase text-primary/70 mb-6" data-id="element-64">
            Today at a glance
          </div>
          <div className="font-serif font-bold text-[88px] md:text-[120px] leading-none text-primary tracking-tight flex items-baseline gap-5" data-id="element-65">
            <CountUp value={totalSubmissions} data-id="element-66" />
            <span className="font-serif font-medium text-2xl md:text-3xl text-primary/50 italic" data-id="element-67">
              submissions
            </span>
          </div>
          <p className="font-serif font-medium text-2xl md:text-3xl text-ink-70 leading-snug mt-8 max-w-2xl" data-id="element-68">
            The floor sent{' '}
            <span className="text-primary font-bold" data-id="element-69">
              {totalSubmissions} loan files
            </span>{' '}
            to underwriting and converted{' '}
            <span className="text-primary font-bold" data-id="element-70">{totalLeads} leads</span>{' '}
            across{' '}
            <span className="text-primary font-bold" data-id="element-71">
              {valid.length} loan officers
            </span>{' '}
            — a{' '}
            <span className="text-signal font-bold" data-id="element-72">
              {liftPct}% {direction}
            </span>{' '}
            on yesterday, led by{' '}
            <span className="text-primary font-bold italic" data-id="element-73">{lead?.name}</span>.
          </p>
        </div>

        <div className="col-span-12 lg:col-span-4 flex flex-col divide-y divide-ink-10 border-t border-b border-ink-10" data-id="element-74">
          <MicroStat label="Leads Converted" value={`${totalLeads}`} delta={leadsDelta} hint="new applications today" data-id="element-75" />
          <MicroStat label="Borrower Hours" value={formatHm(totalBorrower)} delta={borrowerDelta} hint="on-call time" data-id="element-76" />
          <MicroStat label="Team Production" value={`${avgProduction.toFixed(1)}`} delta={productionDelta} hint="avg production score" data-id="element-77" />
          <MicroStat label="Off-Queue Ratio" value={`${offQueueRatio.toFixed(1)}%`} delta={offQueueDelta} inverse hint="lower is better" data-id="element-78" />
        </div>
      </div>
    </motion.section>;
}
function MicroStat({
  label,
  value,
  delta,
  inverse = false,
  hint
}: {
  label: string;
  value: string;
  delta: number;
  inverse?: boolean;
  hint?: string;
}) {
  return <div className="flex items-baseline justify-between py-4 gap-4" data-id="element-79">
      <div className="min-w-0" data-id="element-80">
        <div className="text-xs uppercase tracking-[0.18em] text-ink-70 font-medium" data-id="element-81">
          {label}
        </div>
        {hint && <div className="text-xs text-ink-50 mt-1" data-id="element-82">{hint}</div>}
      </div>
      <div className="flex items-baseline gap-3 flex-shrink-0" data-id="element-83">
        <span className="font-serif font-bold text-2xl text-primary tabular-nums" data-id="element-84">
          {value}
        </span>
        <Delta value={delta} inverse={inverse} data-id="element-85" />
      </div>
    </div>;
}